import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'

import { Box, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { AlertCircleLine, Sparkles } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { useCheckSubscriptionItemsInstalled } from 'modules/marketplace_modules/hooks/useChecksubscriptionItemsInstalled'
import PriceInfo from 'modules/upsell/components/SelfUpsell/CheckoutModal/PriceInfo'
import type { ApiDiscount } from 'modules/upsell/lib/selfUpsell/discountTypes'
import { DEFAULT_DISCOUNT_PERIOD } from 'modules/upsell/lib/selfUpsell/discountTypes'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'
import type { UpsellDiscount, VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'
import type { SelfUpsellConfig } from 'modules/upsell/store/SelfUpsellProvider/types'

type CrossellingSelectorProps = {
  crossSellingAddon?: Plan | undefined
  showPrice: boolean
  selectedCrossSellingAddons: Plan[]
  setSelectedCrossSellingAddons: (crossSellingAddons: Plan[] | ((prev: Plan[]) => Plan[])) => void
  discounts: ApiDiscount[] | undefined
  isMandatory?: boolean
  isLoadingDiscounts?: boolean
}

/**
 * CrossellingSelector Component
 *
 * Displays cross-selling addon options with selection checkboxes.
 * Handles auto-selection logic based on installation status:
 * - Not installed + mandatory → checked by default
 * - Not installed + not mandatory → checked by default
 * - Installed → not checked by default (hidden)
 */
const CrossellingSelector = ({
  crossSellingAddon,
  showPrice,
  selectedCrossSellingAddons,
  setSelectedCrossSellingAddons,
  discounts,
  isMandatory = false,
  isLoadingDiscounts = false,
}: CrossellingSelectorProps) => {
  const {
    configPlan,
    setConfigPlan,
    period,
    toggleCrossSellingPlanSelect,
    toggleMandatoryCrossSellingPlanSelect,
    currency,
    allCrossSellingAddons,
    allMandatoryCrossSellingAddons,
    seats,
    currentSeats,
    availableCrossSellingAddons,
    availableMandatoryCrossSellingAddons,
  } = useSelfUpsellContext()

  // Get the appropriate available addons list based on mandatory status
  const availableAddons = isMandatory
    ? availableMandatoryCrossSellingAddons
    : availableCrossSellingAddons

  // Find the addon reference for installation check
  const addonReference = availableAddons.filter(
    (addon) => addon.internalTitle === crossSellingAddon?.internalTitle
  )

  // Check if this specific addon is already installed
  const { isInstalled } = useCheckSubscriptionItemsInstalled(
    addonReference.length > 0 ? addonReference : [],
    {}
  )

  // Check installation status for all non-mandatory addons at once
  // This allows us to select all non-mandatory addons that aren't installed
  const { moduleStatusMap: nonMandatoryModuleStatusMap } = useCheckSubscriptionItemsInstalled(
    !isMandatory && availableCrossSellingAddons.length > 0
      ? availableCrossSellingAddons.map((addon) => [addon])
      : [],
    {}
  )

  // Track previous config plan to detect manual deselection
  // Used to prevent auto-selection after user manually deselects an addon
  const prevConfigPlanRef = useRef<string[]>([])
  // Track addons that have been manually deselected to prevent re-selection
  const manuallyDeselectedRef = useRef<Set<string>>(new Set())

  // Preserve discount when first loaded, so it persists even when deselected
  // When addon is deselected, estimateProrate doesn't return discount, so we use preserved one
  const [preservedDiscount, setPreservedDiscount] = useState<UpsellDiscount | undefined>(undefined)

  // Helper: Normalize configPlan.addon to array (handles both string and string[] types)
  const getAddonIds = useCallback((): string[] => {
    if (!configPlan.addon) return []
    return Array.isArray(configPlan.addon) ? configPlan.addon : [configPlan.addon]
  }, [configPlan.addon])

  // Helper: Get all selected mandatory addon IDs
  // These are always included in config plan to ensure mandatory addons are subscribed
  const getSelectedMandatoryIds = useCallback((): string[] => {
    return allMandatoryCrossSellingAddons.f25Addons
      .filter((a) => a.isSelected)
      .map((a) => a.pricing.getPlanId(period) || '')
      .filter(Boolean)
  }, [allMandatoryCrossSellingAddons.f25Addons, period])

  // Helper: Toggle addon selection based on mandatory status
  // Uses different toggle functions for mandatory vs non-mandatory addons
  const toggleAddonSelection = useCallback(() => {
    if (!crossSellingAddon) return
    if (isMandatory) {
      toggleMandatoryCrossSellingPlanSelect(crossSellingAddon)
    } else {
      toggleCrossSellingPlanSelect(crossSellingAddon)
    }
  }, [
    crossSellingAddon,
    isMandatory,
    toggleCrossSellingPlanSelect,
    toggleMandatoryCrossSellingPlanSelect,
  ])

  /**
   * Main effect: Auto-select addons based on installation status
   *
   * Logic:
   * 1. If installed → Remove from config plan and selection (don't show)
   * 2. If not installed → Auto-select by default (unless user just deselected)
   * 3. Sync selection state if addon is in config plan but not in selected list
   */
  useEffect(() => {
    if (!crossSellingAddon) return

    const planId = crossSellingAddon.pricing.getPlanId(period)
    if (!planId) return

    const addonIds = getAddonIds()
    const isInConfigPlan = addonIds.includes(planId)
    const isInSelectedList = selectedCrossSellingAddons.some(
      (p) => p.pricing.getPlanId(period) === planId
    )

    // Detect manual deselection: was in previous config plan but not in current
    // This prevents auto-selection after user manually deselects
    const wasInPreviousConfigPlan = prevConfigPlanRef.current.includes(planId)
    const wasJustDeselected = wasInPreviousConfigPlan && !isInConfigPlan

    // Case 1: Addon is installed → Remove from config plan and selection
    if (isInstalled) {
      if (isInConfigPlan) {
        // Use functional update to read latest state
        setConfigPlan((prevConfig: SelfUpsellConfig) => {
          const prevAddonIds = Array.isArray(prevConfig.addon)
            ? prevConfig.addon
            : prevConfig.addon
              ? [prevConfig.addon]
              : []
          const filteredAddons = prevAddonIds.filter((id: string) => id !== planId)
          const uniqueAddons = Array.from(new Set(filteredAddons.filter(Boolean)))
          return {
            ...prevConfig,
            addon: uniqueAddons.length > 0 ? uniqueAddons : undefined,
          }
        })
      }
      if (isInSelectedList) {
        setSelectedCrossSellingAddons((prevSelected: Plan[]) =>
          prevSelected.filter((p: Plan) => p.pricing.getPlanId(period) !== planId)
        )
      }
      prevConfigPlanRef.current = getAddonIds()
      return
    }

    // Case 2: Addon is not installed → Check by default (mandatory or not)
    // Don't auto-select if user just manually deselected it
    if (
      !crossSellingAddon.isSelected &&
      !isInSelectedList &&
      !isInConfigPlan &&
      !wasJustDeselected &&
      !manuallyDeselectedRef.current.has(planId)
    ) {
      // For non-mandatory addons, only auto-select the first available addon
      if (!isMandatory && nonMandatoryModuleStatusMap) {
        // Find all non-mandatory addons that aren't installed
        const availableToSelect = allCrossSellingAddons.f25Addons.filter((addon) => {
          if (addon.isSelected) return false
          const addonStatus = nonMandatoryModuleStatusMap.get(addon.internalTitle)
          if (addonStatus?.isInstalled) return false
          const addonPlanId = addon.pricing.getPlanId(period)
          if (!addonPlanId) return false
          if (manuallyDeselectedRef.current.has(addonPlanId)) return false
          const wasInPrevious = prevConfigPlanRef.current.includes(addonPlanId)
          const isInCurrent = addonIds.includes(addonPlanId)
          if (wasInPrevious && !isInCurrent) return false
          return true
        })

        // Check if another non-mandatory addon is already selected
        const anotherAlreadySelected = allCrossSellingAddons.f25Addons.some(
          (addon) => addon.isSelected && addon.pricing.getPlanId(period) !== planId
        )

        // Only auto-select if this is the first available addon AND no other is already selected
        const firstAvailable = availableToSelect[0]
        if (
          firstAvailable &&
          firstAvailable.pricing.getPlanId(period) === planId &&
          !anotherAlreadySelected
        ) {
          toggleCrossSellingPlanSelect(firstAvailable)
          setSelectedCrossSellingAddons((prevSelected: Plan[]) => {
            const existingIds = new Set(
              prevSelected.map((p: Plan) => p.pricing.getPlanId(period)).filter(Boolean)
            )
            if (existingIds.has(planId)) return prevSelected
            return [...prevSelected, firstAvailable]
          })
          setConfigPlan((prevConfig: SelfUpsellConfig) => {
            const prevAddonIds = Array.isArray(prevConfig.addon)
              ? prevConfig.addon
              : prevConfig.addon
                ? [prevConfig.addon]
                : []
            const mandatoryIds = getSelectedMandatoryIds()
            const allAddonIds = [planId, ...prevAddonIds, ...mandatoryIds]
            const uniqueAddons = Array.from(new Set(allAddonIds.filter(Boolean)))
            return {
              ...prevConfig,
              addon: uniqueAddons.length > 0 ? uniqueAddons : undefined,
            }
          })
        }
      } else {
        // For mandatory addons or if status map not available, select just this one
        toggleAddonSelection()
        setSelectedCrossSellingAddons((prevSelected: Plan[]) => {
          const existingIds = new Set(
            prevSelected.map((p: Plan) => p.pricing.getPlanId(period)).filter(Boolean)
          )
          if (existingIds.has(planId)) return prevSelected
          return [...prevSelected, crossSellingAddon]
        })
        // Use functional update to read latest state and include all existing addons
        setConfigPlan((prevConfig: SelfUpsellConfig) => {
          const prevAddonIds = Array.isArray(prevConfig.addon)
            ? prevConfig.addon
            : prevConfig.addon
              ? [prevConfig.addon]
              : []
          const mandatoryIds = getSelectedMandatoryIds()
          const allAddonIds = [planId, ...prevAddonIds, ...mandatoryIds]
          const uniqueAddons = Array.from(new Set(allAddonIds.filter(Boolean)))
          return {
            ...prevConfig,
            addon: uniqueAddons.length > 0 ? uniqueAddons : undefined,
          }
        })
      }
    } else if (isInConfigPlan && !isInSelectedList) {
      // Sync: Addon is in config plan but not in selected list
      // Only add to selection if it wasn't manually deselected (mandatory addons always sync)
      if (isMandatory || !manuallyDeselectedRef.current.has(planId)) {
        if (!crossSellingAddon.isSelected) {
          toggleAddonSelection()
        }
        setSelectedCrossSellingAddons((prevSelected) => {
          const existingIds = new Set(
            prevSelected.map((p) => p.pricing.getPlanId(period)).filter(Boolean)
          )
          if (existingIds.has(planId)) return prevSelected
          return [...prevSelected, crossSellingAddon]
        })
      } else {
        // Was manually deselected — remove from config plan instead of re-adding
        setConfigPlan((prevConfig: SelfUpsellConfig) => {
          const prevAddonIds = Array.isArray(prevConfig.addon)
            ? prevConfig.addon
            : prevConfig.addon
              ? [prevConfig.addon]
              : []
          const filteredAddons = prevAddonIds.filter((id: string) => id !== planId)
          const uniqueAddons = Array.from(new Set(filteredAddons.filter(Boolean)))
          return {
            ...prevConfig,
            addon: uniqueAddons.length > 0 ? uniqueAddons : undefined,
          }
        })
      }
    }

    // Update ref with current config plan for next render
    prevConfigPlanRef.current = getAddonIds()
  }, [
    configPlan.addon,
    crossSellingAddon,
    period,
    isMandatory,
    isInstalled,
    selectedCrossSellingAddons,
    setSelectedCrossSellingAddons,
    getAddonIds,
    getSelectedMandatoryIds,
    toggleAddonSelection,
    toggleCrossSellingPlanSelect,
    setConfigPlan,
    allCrossSellingAddons.f25Addons,
    nonMandatoryModuleStatusMap,
  ])

  // Check installation status for all mandatory addons at once
  // Returns a map of internalTitle -> installation status
  const { moduleStatusMap } = useCheckSubscriptionItemsInstalled(
    availableMandatoryCrossSellingAddons.length > 0
      ? availableMandatoryCrossSellingAddons.map((addon) => [addon])
      : [],
    {}
  )

  /**
   * Cleanup effect: Remove installed mandatory addons from config plan
   *
   * When mandatory addons are installed, they should not appear in the config plan
   * even if they were previously selected. This effect ensures they're removed.
   */
  useEffect(() => {
    if (!configPlan.addon || !moduleStatusMap || availableMandatoryCrossSellingAddons.length === 0)
      return

    // Use functional update to read latest state
    setConfigPlan((prevConfig: SelfUpsellConfig) => {
      const prevAddonIds = Array.isArray(prevConfig.addon)
        ? prevConfig.addon
        : prevConfig.addon
          ? [prevConfig.addon]
          : []

      const installedMandatoryIds = allMandatoryCrossSellingAddons.f25Addons
        .filter((mandatoryAddon) => {
          const mandatoryPlanId = mandatoryAddon.pricing.getPlanId(period)
          if (!mandatoryPlanId || !prevAddonIds.includes(mandatoryPlanId)) return false

          const availableAddon = availableMandatoryCrossSellingAddons.find(
            (available) => available.internalTitle === mandatoryAddon.internalTitle
          )

          return availableAddon && moduleStatusMap.get(availableAddon.internalTitle)?.isInstalled
        })
        .map((a) => a.pricing.getPlanId(period) || '')
        .filter(Boolean)

      if (installedMandatoryIds.length > 0) {
        const filteredAddons = prevAddonIds.filter(
          (id: string) => !installedMandatoryIds.includes(id)
        )
        const uniqueAddons = Array.from(new Set(filteredAddons.filter(Boolean)))
        return {
          ...prevConfig,
          addon: uniqueAddons.length > 0 ? uniqueAddons : undefined,
        }
      }

      return prevConfig
    })
  }, [
    configPlan.addon,
    period,
    allMandatoryCrossSellingAddons.f25Addons,
    availableMandatoryCrossSellingAddons,
    moduleStatusMap,
    setConfigPlan,
  ])

  /**
   * Get discount for the current addon
   *
   * Preserves discount when first loaded so it persists even when addon is deselected.
   * When addon is deselected, estimateProrate doesn't return the discount anymore,
   * so we use the preserved discount to keep showing it.
   */
  const getDiscountForAddon = (): UpsellDiscount | undefined => {
    if (!crossSellingAddon) return preservedDiscount || undefined

    const currentPlanId = crossSellingAddon.pricing.getPlanId(period)

    // Try to find discount in current discounts (from estimateProrate)
    const apiDiscount = discounts?.find((d) => {
      if (!d.lineItemId || d.lineItemId !== currentPlanId) return false
      return true
    })

    // If discount found, preserve it and return it
    if (apiDiscount) {
      const discount: UpsellDiscount = {
        module: crossSellingAddon.internalTitle,
        percentage: apiDiscount.discountPercentage,
        period: String(apiDiscount.period || DEFAULT_DISCOUNT_PERIOD.VALUE),
        periodUnit: String(apiDiscount.periodUnit || DEFAULT_DISCOUNT_PERIOD.UNIT),
      }

      // Preserve the discount if we don't have one yet or if it changed
      if (!preservedDiscount || preservedDiscount.percentage !== discount.percentage) {
        setPreservedDiscount(discount)
      }

      return discount
    }

    // If no discount in current discounts (e.g., when deselected), use preserved discount
    // or try to find a discount from other cross-selling addons (since they share discount rules)
    if (preservedDiscount) return preservedDiscount

    // Fallback: check if any other cross-selling addon has a discount that applies
    const otherAddonDiscount = discounts?.find((d) => {
      if (!d.lineItemId) return false
      // Check if this discount belongs to any cross-selling addon
      return allCrossSellingAddons.f25Addons.some(
        (addon) => addon.pricing.getPlanId(period) === d.lineItemId
      )
    })

    if (otherAddonDiscount) {
      const fallbackDiscount: UpsellDiscount = {
        module: crossSellingAddon.internalTitle,
        percentage: otherAddonDiscount.discountPercentage,
        period: String(otherAddonDiscount.period || DEFAULT_DISCOUNT_PERIOD.VALUE),
        periodUnit: String(otherAddonDiscount.periodUnit || DEFAULT_DISCOUNT_PERIOD.UNIT),
      }
      return fallbackDiscount
    }

    return undefined
  }

  if (!crossSellingAddon) return null

  // Don't show the addon (including mandatory addons) if it's already installed
  if (isInstalled) return null

  /**
   * Handle checkbox/variant change
   *
   * When user clicks checkbox or variant card:
   * - Toggles selection state
   * - Updates selectedCrossSellingAddons list
   * - Updates config plan (adds/removes from subscription)
   * - Prevents changes for mandatory addons (they're always selected)
   */
  const handleVariantChange = (variant: VariantsEnum, event?: ChangeEvent<HTMLInputElement>) => {
    // Mandatory addons cannot be deselected
    if (isMandatory) {
      if (event) {
        event.stopPropagation()
      }
      return
    }

    // Stop event propagation to prevent parent Box onClick from firing
    if (event) {
      event.stopPropagation()
    }

    const wasSelected = crossSellingAddon.isSelected
    const currentPlanId = crossSellingAddon.pricing.getPlanId(period)

    if (wasSelected) {
      // Deselecting the current addon
      toggleCrossSellingPlanSelect(crossSellingAddon)
      setSelectedCrossSellingAddons((prevSelected: Plan[]) =>
        prevSelected.filter((addon: Plan) => addon.pricing.getPlanId(period) !== currentPlanId)
      )
    } else {
      // Single-select: deselect all other non-mandatory addons first
      const otherSelectedAddons = allCrossSellingAddons.f25Addons.filter(
        (addon) => addon.isSelected && addon.pricing.getPlanId(period) !== currentPlanId
      )
      otherSelectedAddons.forEach((addon) => {
        toggleCrossSellingPlanSelect(addon)
        manuallyDeselectedRef.current.add(addon.pricing.getPlanId(period) || '')
      })

      // Select the current addon
      toggleCrossSellingPlanSelect(crossSellingAddon)
      setSelectedCrossSellingAddons((prevSelected: Plan[]) => {
        // Preserve mandatory addon selections, replace only non-mandatory
        const mandatoryInternalTitles = new Set(
          allMandatoryCrossSellingAddons.f25Addons.map((a) => a.internalTitle)
        )
        const mandatorySelections = prevSelected.filter((p: Plan) =>
          mandatoryInternalTitles.has(p.internalTitle)
        )
        return [...mandatorySelections, crossSellingAddon]
      })
    }

    crossSellingAddon.setActiveVariant(variant)
    // Pass the new state (inverted wasSelected) to update config plan
    handleToggleCrossSellingPlan(!wasSelected)
  }

  /**
   * Update config plan when addon is added/removed
   *
   * Always includes mandatory addons in the config plan.
   * Uses isAdding flag to know if we're adding or removing (since toggle happens before this call).
   * Uses functional update to read latest state and prevent race conditions.
   */
  const handleToggleCrossSellingPlan = (isAdding: boolean) => {
    if (isInstalled) return

    const planId = crossSellingAddon.pricing.getPlanId(period) || ''
    const mandatoryIds = getSelectedMandatoryIds()

    // Use functional update to read latest state
    setConfigPlan((prevConfig: SelfUpsellConfig) => {
      const prevAddonIds = Array.isArray(prevConfig.addon)
        ? prevConfig.addon
        : prevConfig.addon
          ? [prevConfig.addon]
          : []

      let allAddonIds: string[]
      if (isAdding) {
        // Single-select: remove all other non-mandatory addon IDs, keep only this one + mandatory
        const otherNonMandatoryIds = allCrossSellingAddons.f25Addons
          .filter((addon) => addon.pricing.getPlanId(period) !== planId)
          .map((addon) => addon.pricing.getPlanId(period) || '')
          .filter(Boolean)
        const filteredPrev = prevAddonIds.filter((id: string) => !otherNonMandatoryIds.includes(id))
        allAddonIds = [planId, ...filteredPrev, ...mandatoryIds]
        // Remove from manually deselected set if user re-selects it
        manuallyDeselectedRef.current.delete(planId)
      } else {
        // Removing from config plan (but keep mandatory addons)
        allAddonIds = [...prevAddonIds.filter((id: string) => id !== planId), ...mandatoryIds]
        // Track that this addon was manually deselected
        manuallyDeselectedRef.current.add(planId)
      }

      const uniqueAddons = Array.from(new Set(allAddonIds.filter(Boolean)))
      return {
        ...prevConfig,
        addon: uniqueAddons.length > 0 ? uniqueAddons : undefined,
      }
    })
  }

  // Check if a specific variant (Business/Enterprise) is available for the current addon
  const isCrossSellingVariantAvailable = (variant: VariantsEnum) => {
    const addons = isMandatory
      ? allMandatoryCrossSellingAddons.f25Addons
      : allCrossSellingAddons.f25Addons
    return addons.some((a) => a.variantTypes.includes(variant))
  }

  /**
   * Render variant card (Business or Enterprise)
   *
   * Displays:
   * - Mandatory badge if required
   * - Variant name and description
   * - Checkbox for selection
   * - Price info with discount (if available)
   * - Loading spinner while discounts are being fetched
   */
  const renderVariantCard = (variant: VariantsEnum) => {
    const isSelected = isMandatory || crossSellingAddon.isSelected
    const variantPlan = crossSellingAddon.getSpecificVariant(variant)

    return (
      <Box
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: isSelected ? 'viridian1000' : 'grey400',
          },
        }}
        borderRadius={{ all: 'abs016' }}
        background={isSelected ? 'viridian100' : 'grey000White'}
        style={{ overflow: 'hidden' }}
      >
        {isMandatory && (
          <Box
            background={variant === ENTERPRISE_VARIANT ? 'grey100' : 'grey300'}
            paddingX="s12"
            paddingY="s8"
            flexDirection="row"
            gap="s8"
            alignItems="center"
          >
            <Box
              borderRadius={{ all: 'abs012' }}
              background="grey200"
              padding="s4"
              style={{
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertCircleLine style={{ width: '12px', height: '12px' }} />
            </Box>
            <Text.Normal
              size="200"
              weight="medium"
              color={variant === ENTERPRISE_VARIANT ? 'grey800' : 'grey1000'}
            >
              {i18n.t('self_upsell.second_step.left_side.required')}
            </Text.Normal>
          </Box>
        )}
        <Box
          padding="s16"
          gap="s8"
          justifyContent="spaceBetween"
          onClick={() => !isMandatory && handleVariantChange(variant)}
          style={{
            height: variant === ENTERPRISE_VARIANT ? '88px' : undefined,
            cursor: isMandatory ? 'default' : 'pointer',
          }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
            {variant === ENTERPRISE_VARIANT ? (
              <>
                <Text.Normal size="200" weight="semibold" color="grey1000">
                  {ENTERPRISE_VARIANT}
                </Text.Normal>
                <Box flexDirection="row" gap="s4" alignItems="center">
                  <F0TagRaw text="Best value" icon={Sparkles} />
                </Box>
              </>
            ) : (
              <Box flexDirection="column" gap="s8">
                <Text.Normal size="200" weight="semibold" color="grey1000">
                  {crossSellingAddon.title}
                </Text.Normal>
                <Text.Normal size="100" color="grey700">
                  {crossSellingAddon.description}
                </Text.Normal>
              </Box>
            )}
            <Box onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onChange={(e) => handleVariantChange(variant, e)}
                disabled={isMandatory}
              />
            </Box>
          </Box>
          {showPrice && (
            <>
              {variant === ENTERPRISE_VARIANT ? (
                <>
                  {isLoadingDiscounts ? (
                    <Box justifyContent="center" alignItems="center" padding="s4">
                      <Spinner size="small" />
                    </Box>
                  ) : (
                    <PriceInfo
                      variant={variantPlan}
                      period={period}
                      currency={currency}
                      seats={seats}
                      currentSeats={currentSeats}
                      discount={getDiscountForAddon()}
                    />
                  )}
                </>
              ) : (
                <Box flexDirection="row" justifyContent="flexEnd" alignSelf="flexEnd">
                  {isLoadingDiscounts ? (
                    <Box justifyContent="center" alignItems="center" padding="s4">
                      <Spinner size="small" />
                    </Box>
                  ) : (
                    <PriceInfo
                      variant={variantPlan}
                      period={period}
                      currency={currency}
                      seats={seats}
                      currentSeats={currentSeats}
                      discount={getDiscountForAddon()}
                      fromCrossSelling
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    )
  }

  // Render available variants (Enterprise and/or Business)
  return (
    <Box gap="s6">
      <Box gap="s8">
        {isCrossSellingVariantAvailable(ENTERPRISE_VARIANT) &&
          renderVariantCard(ENTERPRISE_VARIANT)}
        {isCrossSellingVariantAvailable(BUSINESS_VARIANT) && renderVariantCard(BUSINESS_VARIANT)}
      </Box>
    </Box>
  )
}

export default CrossellingSelector
